// =============================================================================
// features/admin/feature-flags/AudienceSummary.tsx — Semântica E/OU + resumo
// de impacto exibidos antes de salvar (F32-S04, DoD).
//
// Texto curto e sempre visível (não é tooltip escondido) — severidade muda a
// cor do callout: neutral/info/warning conforme docs/18-design-system.md §7.
// =============================================================================

import * as React from 'react';

import type { AudienceImpactSummary } from './audienceImpact';

const SEVERITY_STYLE: Record<
  AudienceImpactSummary['severity'],
  { bg: string; border: string; text: string }
> = {
  neutral: { bg: 'var(--surface-muted)', border: 'var(--border)', text: 'var(--text-3)' },
  info: { bg: 'var(--info-bg)', border: 'var(--info)', text: 'var(--info)' },
  warning: { bg: 'var(--warning-bg)', border: 'var(--warning)', text: 'var(--warning)' },
  danger: { bg: 'var(--danger-bg)', border: 'var(--danger)', text: 'var(--danger)' },
};

interface AudienceSummaryProps {
  impact: AudienceImpactSummary;
}

export function AudienceSummary({ impact }: AudienceSummaryProps): React.JSX.Element {
  const style = SEVERITY_STYLE[impact.severity];

  return (
    <div className="flex flex-col gap-3">
      {/* ── Semântica E/OU — sempre visível, texto curto ─────────────────── */}
      <p className="font-sans text-xs text-ink-3">
        Eixos diferentes (papel, cidade, organização, usuário) combinam com{' '}
        <strong className="text-ink-2">E</strong>. Valores dentro do mesmo eixo combinam com{' '}
        <strong className="text-ink-2">OU</strong>.
      </p>

      {/* ── Resumo de impacto ─────────────────────────────────────────────── */}
      <div
        role="status"
        className="flex flex-col gap-1.5 rounded-sm border-l-[3px] px-4 py-3"
        style={{ background: style.bg, borderLeftColor: style.border }}
      >
        <p className="font-sans text-sm font-semibold" style={{ color: style.text }}>
          {impact.headline}
        </p>
        <p className="font-sans text-xs text-ink-2">{impact.sentence}</p>
        <p className="font-sans text-xs font-medium" style={{ color: style.text }}>
          {impact.hint}
        </p>
      </div>

      {/* ── Chips por eixo preenchido (repetição visual da frase acima) ──── */}
      {impact.filledAxesCount > 0 && (
        <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {impact.axes
            .filter((a) => !a.isEmpty)
            .map((axis) => (
              <div key={axis.key} className="flex flex-col gap-0.5">
                <dt className="font-sans text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-ink-4">
                  {axis.label}
                </dt>
                <dd
                  className="font-sans text-xs text-ink-2 truncate"
                  title={axis.displayValues.join(', ')}
                >
                  {axis.displayValues.join(' OU ')}
                </dd>
              </div>
            ))}
        </dl>
      )}
    </div>
  );
}
